import { getPagesToShow } from "@/utils/pagination";
import type { Meta } from "src/@types/paginated";

import {
  FIRST_PAGE,
  NAVIGATE_BUTTON_CLASS,
  PAGE_CELL_CLASS,
  PAGE_CELL_CLICKABLE_CLASS,
  PAGE_CELL_SELECTED_CLASS,
  PAGINATION_ELLIPSIS,
  STORE_EXCHANGE_LABELS,
} from "../types";
import { StoreExchangePageSizeSelect } from "./store-exchange-page-size-select";

const pageClass = (shown: string | number, currentPage: number): string => {
  const isEllipsis: boolean = shown === PAGINATION_ELLIPSIS;

  return [
    PAGE_CELL_CLASS,
    shown === currentPage ? PAGE_CELL_SELECTED_CLASS : "",
    isEllipsis ? "" : PAGE_CELL_CLICKABLE_CLASS,
  ]
    .filter(Boolean)
    .join(" ");
};

interface StoreExchangePaginationProps {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly isFetching: boolean;
  readonly onPageChange: (page: number) => void;
  readonly onPageSizeChange: (size: number) => void;
}

export function StoreExchangePagination({
  page,
  pageSize,
  total,
  isFetching,
  onPageChange,
  onPageSizeChange,
}: StoreExchangePaginationProps) {
  const lastPage: number = Math.max(FIRST_PAGE, Math.ceil(total / pageSize));
  const meta: Meta = {
    currentPage: page,
    lastPage,
    perPage: pageSize,
    total,
    prev: page > FIRST_PAGE ? page - 1 : null,
    next: page < lastPage ? page + 1 : null,
  };

  return (
    <div className="flex w-full items-center justify-between px-3 py-2 text-xs border-t border-slate-200">
      <div className="flex text-sm">
        {total > 0 && (
          <>
            <button
              className={`${NAVIGATE_BUTTON_CLASS} rounded-bl-lg`}
              onClick={() => meta.prev !== null && onPageChange(meta.prev)}
              disabled={meta.prev === null || isFetching}
            >
              {STORE_EXCHANGE_LABELS.previousPage}
            </button>
            {getPagesToShow(meta).map(
              (shown: string | number, index: number) => (
                <span
                  key={`${shown}-${index}`}
                  className={pageClass(shown, meta.currentPage)}
                  onClick={() =>
                    typeof shown === "number" && onPageChange(shown)
                  }
                >
                  {shown}
                </span>
              ),
            )}
            <button
              className={`${NAVIGATE_BUTTON_CLASS} rounded-br-lg`}
              onClick={() => meta.next !== null && onPageChange(meta.next)}
              disabled={meta.next === null || isFetching}
            >
              {STORE_EXCHANGE_LABELS.nextPage}
            </button>
          </>
        )}
      </div>

      <div className="flex items-center gap-4">
        <StoreExchangePageSizeSelect
          value={pageSize}
          onChange={onPageSizeChange}
        />
        <span className="text-slate-600">
          Página {page} de {lastPage} ({total}{" "}
          {total === 1
            ? STORE_EXCHANGE_LABELS.exchangeSingular
            : STORE_EXCHANGE_LABELS.exchangePlural}
          )
        </span>
      </div>
    </div>
  );
}

